import { useState, useEffect, useRef, useCallback } from "react";
import { X, ShieldCheck, BarChart2, Megaphone, SlidersHorizontal } from "lucide-react";
import Switch from "../Switch/Switch";
import "./CookieSettings.scss";

/**
 * Definície kategórií cookies – rozšíriteľné podľa potreby.
 * 'necessary' je vždy povolené a nedá sa vypnúť.
 */
const CATEGORIES = [
  {
    key: "necessary",
    icon: ShieldCheck,
    title: "Nevyhnutné cookies",
    description:
      "Tieto cookies sú potrebné pre základnú funkčnosť stránky (prihlásenie, košík, bezpečnosť). Nie je možné ich vypnúť.",
    alwaysOn: true,
  },
  {
    key: "analytics",
    icon: BarChart2,
    title: "Analytické cookies",
    description:
      "Pomáhajú nám pochopiť, ako návštevníci používajú stránku (napr. Google Analytics 4). Všetky údaje sú anonymizované.",
  },
  {
    key: "marketing",
    icon: Megaphone,
    title: "Marketingové cookies",
    description:
      "Umožňujú zobrazovanie relevantných reklám na iných platformách. Bez súhlasu sa personalizovaná reklama nezobrazuje.",
  },
  {
    key: "preferences",
    icon: SlidersHorizontal,
    title: "Preferenčné cookies",
    description:
      "Pamätajú si vaše nastavenia (jazyk, región, zobrazenie) pre pohodlnejší zážitok pri ďalšej návšteve.",
  },
];

/**
 * CookieSettings – modálne okno s granulárnym nastavením kategórií cookies.
 *
 * Accessibility:
 *  - focus trap: fokus zostáva vnútri modálu
 *  - Escape zatvorí modal
 *  - role="dialog" + aria-modal
 */
function CookieSettings({ consent, onSave, onAcceptAll, onRejectAll, onClose }) {
  const [local, setLocal] = useState({
    analytics: consent?.analytics ?? false,
    marketing: consent?.marketing ?? false,
    preferences: consent?.preferences ?? false,
  });

  const modalRef = useRef(null);
  const closeRef = useRef(null);

  // Fokus na zatvárací gombík pri otvorení
  useEffect(() => {
    closeRef.current?.focus();
  }, []);

  // Escape zatvára modal
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  // Zabrání scrollu pod modálom
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  // Focus trap – fokus neopustí modál
  const handleKeyDown = useCallback((e) => {
    if (e.key !== "Tab") return;
    const focusable = modalRef.current?.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (!focusable || focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }, []);

  const toggle = (key) => {
    setLocal((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSave = () => {
    onSave(local);
  };

  return (
    <div
      className="cookie-settings__overlay"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={modalRef}
        className="cookie-settings"
        role="dialog"
        aria-modal="true"
        aria-label="Nastavenia cookies"
        onKeyDown={handleKeyDown}
      >
        {/* Hlavička */}
        <div className="cookie-settings__header">
          <h2 className="cookie-settings__title">Nastavenia cookies</h2>
          <button
            ref={closeRef}
            className="cookie-settings__close"
            onClick={onClose}
            aria-label="Zavrieť nastavenia cookies"
          >
            <X size={18} />
          </button>
        </div>

        {/* Úvodný text */}
        <p className="cookie-settings__intro">
          Vyberte, ktoré kategórie cookies chcete povoliť. Vaše rozhodnutie môžete
          kedykoľvek zmeniť v nastaveniach. Viac informácií nájdete v našej{" "}
          <a
            href="/cookie-politika"
            className="cookie-settings__link"
            target="_blank"
            rel="noopener noreferrer"
          >
            Cookie politike
          </a>
          .
        </p>

        {/* Kategórie */}
        <div className="cookie-settings__categories">
          {CATEGORIES.map(({ key, icon: Icon, title, description, alwaysOn }) => (
            <div key={key} className="cookie-settings__category">
              <div className="cookie-settings__category-icon" aria-hidden="true">
                <Icon size={18} />
              </div>
              <div className="cookie-settings__category-body">
                <div className="cookie-settings__category-header">
                  <span className="cookie-settings__category-title">{title}</span>
                  {alwaysOn ? (
                    <span
                      className="cookie-settings__always-on"
                      aria-label="Vždy povolené"
                    >
                      Vždy aktívne
                    </span>
                  ) : (
                    <Switch
                      checked={local[key]}
                      onChange={() => toggle(key)}
                      aria-label={`Prepnúť ${title}`}
                    />
                  )}
                </div>
                <p className="cookie-settings__category-desc">{description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Akcie */}
        <div className="cookie-settings__footer">
          <button
            className="cookie-settings__action cookie-settings__action--ghost"
            onClick={onRejectAll}
          >
            Odmietnuť všetko
          </button>
          <div className="cookie-settings__footer-right">
            <button
              className="cookie-settings__action cookie-settings__action--save"
              onClick={handleSave}
            >
              Uložiť nastavenia
            </button>
            <button
              className="cookie-settings__action cookie-settings__action--accept"
              onClick={onAcceptAll}
            >
              Prijať všetko
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CookieSettings;
